import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCalculator, FaPiggyBank } from 'react-icons/fa';

const FinancialCalculators = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('emi'); // 'emi' or 'savings'

    // EMI State
    const [loanAmount, setLoanAmount] = useState(250000);
    const [loanTenure, setLoanTenure] = useState(3);
    const [loanInterest, setLoanInterest] = useState(11.3);

    // Savings State
    const [fdAmount, setFdAmount] = useState(50000);
    const [savingsTenure, setSavingsTenure] = useState(5);
    const [savingsInterest, setSavingsInterest] = useState(7.5);

    // Calculated Values
    const [emiResult, setEmiResult] = useState({ emi: 0, totalInterest: 0, totalPayment: 0 });
    const [savingsResult, setSavingsResult] = useState({ invested: 0, interest: 0, total: 0 });

    // EMI Calculation
    useEffect(() => {
        const principal = Number(loanAmount) || 0;
        const rate = (Number(loanInterest) || 0) / 12 / 100;
        const time = (Number(loanTenure) || 0) * 12;

        if (principal <= 0 || time <= 0) {
            setEmiResult({ emi: 0, totalInterest: 0, totalPayment: 0 });
            return;
        }

        if (rate === 0) {
            const emi = principal / time;
            setEmiResult({
                emi: Math.round(emi),
                totalInterest: 0,
                totalPayment: Math.round(principal)
            });
        } else {
            const emi = (principal * rate * Math.pow(1 + rate, time)) / (Math.pow(1 + rate, time) - 1);
            const totalPayment = emi * time;
            const totalInterest = totalPayment - principal;
            setEmiResult({
                emi: Math.round(emi),
                totalInterest: Math.round(totalInterest),
                totalPayment: Math.round(totalPayment)
            });
        }
    }, [loanAmount, loanTenure, loanInterest]);

    // FD Calculation (Compounded Annually)
    useEffect(() => {
        const principal = Number(fdAmount) || 0;
        const rate = Number(savingsInterest) || 0;
        const years = Number(savingsTenure) || 0;

        if (principal <= 0 || years <= 0) {
            setSavingsResult({ invested: 0, interest: 0, total: 0 });
            return;
        }

        // Standard FD formula: A = P(1 + r/100)^t
        const maturityAmount = principal * Math.pow(1 + (rate / 100), years);
        const interestEarned = maturityAmount - principal;

        setSavingsResult({
            invested: Math.round(principal),
            interest: Math.max(0, Math.round(interestEarned)),
            total: Math.round(maturityAmount)
        });

    }, [fdAmount, savingsTenure, savingsInterest]);


    // Donut Chart Component
    const DonutChart = ({ val1, val2, color1, color2, label, percentage }) => {
        const total = val1 + val2;
        if (total === 0) return null;

        const angle1 = (val1 / total) * 360;

        return (
            <div className="relative flex items-center justify-center" style={{ width: '180px', height: '180px' }}>
                {/* The Ring */}
                <div className="w-full h-full rounded-full" style={{
                    background: `conic-gradient(${color1} 0deg ${angle1}deg, ${color2} ${angle1}deg 360deg)`
                }}></div>

                {/* The Hole (White Center) */}
                <div className="absolute rounded-full flex flex-col items-center justify-center shadow-sm bg-white"
                    style={{ width: '120px', height: '120px' }}>
                    <span className="text-2xl font-extrabold text-gray-800">{percentage}%</span>
                    <span className="text-xs font-bold text-gray-400 tracking-widest uppercase mt-1">{label}</span>
                </div>
            </div>
        );
    };

    // Format currency
    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(val);
    };

    return (
        <section className="py-12 md:py-20 bg-gray-50 relative overflow-hidden">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center mb-8 md:mb-12" data-aos="fade-up">
                    <span className="text-secondary font-bold tracking-widest uppercase text-xs mb-3 block">{t('financialCalculators.badge')}</span>
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary-dark mb-4 filter drop-shadow-sm font-display">
                        {t('financialCalculators.heading')}
                    </h2>
                    <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
                        {t('financialCalculators.subheading')}
                    </p>
                </div>

                <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col" data-aos="fade-up">

                    {/* Tabs Header */}
                    <div className="flex">
                        <button
                            onClick={() => setActiveTab('emi')}
                            className={`flex-1 py-4 md:py-5 px-3 text-center font-bold text-sm md:text-lg transition-colors ${activeTab === 'emi' ? 'bg-primary text-white' : 'bg-transparent text-gray-500 hover:text-primary hover:bg-gray-50'}`}
                        >
                            <div className="flex items-center justify-center gap-2 flex-wrap">
                                <FaCalculator className="flex-shrink-0" /> <span>{t('financialCalculators.emiTab')}</span>
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('savings')}
                            className={`flex-1 py-4 md:py-5 px-3 text-center font-bold text-sm md:text-lg transition-colors ${activeTab === 'savings' ? 'bg-primary text-white' : 'bg-transparent text-gray-500 hover:text-primary hover:bg-gray-50'}`}
                        >
                            <div className="flex items-center justify-center gap-2 flex-wrap">
                                <FaPiggyBank className="flex-shrink-0" /> <span>{t('financialCalculators.savingsTab')}</span>
                            </div>
                        </button>
                    </div>

                    {/* Main Content: Inputs + Results stacked on mobile, side-by-side on md+ */}
                    <div className="flex flex-col md:flex-row">

                        {/* Input Section */}
                        <div className="flex-1 p-5 sm:p-8 md:p-12 bg-white">
                            {activeTab === 'emi' ? (
                                <div className="space-y-8">
                                    {/* Loan Amount */}
                                    <div>
                                        <div className="flex justify-between items-center mb-3">
                                            <label className="text-gray-700 font-bold text-sm md:text-base">{t('financialCalculators.loanAmount')}</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-primary font-bold text-sm">₹</span>
                                                <input
                                                    type="number"
                                                    value={loanAmount || ""}
                                                    onChange={(e) => setLoanAmount(e.target.value === "" ? "" : Number(e.target.value))}
                                                    className="w-32 md:w-40 py-2 pl-7 pr-2 text-right bg-blue-50 text-primary font-bold text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                                />
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            min="10000" max="10000000" step="50000"
                                            value={Number(loanAmount) || 10000}
                                            onChange={(e) => setLoanAmount(Number(e.target.value))}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                                        />
                                        <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium">
                                            <span>₹10K</span>
                                            <span>₹1 Cr</span>
                                        </div>
                                    </div>

                                    {/* Tenure */}
                                    <div>
                                        <div className="flex justify-between items-center mb-3">
                                            <label className="text-gray-700 font-bold text-sm md:text-base">{t('financialCalculators.tenure')}</label>
                                            <input
                                                type="number"
                                                value={loanTenure || ""}
                                                onChange={(e) => setLoanTenure(e.target.value === "" ? "" : Number(e.target.value))}
                                                className="w-20 md:w-24 py-2 px-3 text-center bg-blue-50 text-primary font-bold text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                            />
                                        </div>
                                        <input
                                            type="range"
                                            min="1" max="30" step="1"
                                            value={loanTenure}
                                            onChange={(e) => setLoanTenure(Number(e.target.value))}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                                        />
                                        <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium">
                                            <span>1 {t('financialCalculators.yr')}</span>
                                            <span>30 {t('financialCalculators.yrs')}</span>
                                        </div>
                                    </div>

                                    {/* Interest Rate */}
                                    <div>
                                        <div className="flex justify-between items-center mb-3">
                                            <label className="text-gray-700 font-bold text-sm md:text-base">{t('financialCalculators.interestRate')}</label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    value={loanInterest || ""}
                                                    onChange={(e) => setLoanInterest(e.target.value === "" ? "" : Number(e.target.value))}
                                                    step="0.1"
                                                    className="w-24 md:w-28 py-2 pl-3 pr-7 text-right bg-blue-50 text-primary font-bold text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                                />
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary font-bold text-sm">%</span>
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            min="5" max="20" step="0.1"
                                            value={loanInterest}
                                            onChange={(e) => setLoanInterest(Number(e.target.value))}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                                        />
                                        <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium">
                                            <span>5%</span>
                                            <span>20%</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    {/* FD Amount */}
                                    <div>
                                        <div className="flex justify-between items-center mb-3">
                                            <label className="text-gray-700 font-bold text-sm md:text-base">{t('financialCalculators.investmentAmount')}</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary font-bold text-sm">₹</span>
                                                <input
                                                    type="number"
                                                    value={fdAmount || ""}
                                                    onChange={(e) => setFdAmount(e.target.value === "" ? "" : Number(e.target.value))}
                                                    className="w-32 md:w-40 py-2 pl-7 pr-2 text-right bg-yellow-50 text-secondary font-bold text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/20"
                                                />
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            min="1000" max="1000000" step="5000"
                                            value={fdAmount}
                                            onChange={(e) => setFdAmount(Number(e.target.value))}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-secondary"
                                        />
                                        <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium">
                                            <span>₹1K</span>
                                            <span>₹10L</span>
                                        </div>
                                    </div>

                                    {/* Tenure */}
                                    <div>
                                        <div className="flex justify-between items-center mb-3">
                                            <label className="text-gray-700 font-bold text-sm md:text-base">{t('financialCalculators.period')}</label>
                                            <input
                                                type="number"
                                                value={savingsTenure || ""}
                                                onChange={(e) => setSavingsTenure(e.target.value === "" ? "" : Number(e.target.value))}
                                                className="w-20 md:w-24 py-2 px-3 text-center bg-yellow-50 text-secondary font-bold text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/20"
                                            />
                                        </div>
                                        <input
                                            type="range"
                                            min="1" max="10" step="1"
                                            value={Number(savingsTenure) || 1}
                                            onChange={(e) => setSavingsTenure(Number(e.target.value))}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-secondary"
                                        />
                                        <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium">
                                            <span>1 {t('financialCalculators.yr')}</span>
                                            <span>10 {t('financialCalculators.yrs')}</span>
                                        </div>
                                    </div>

                                    {/* Interest Rate */}
                                    <div>
                                        <div className="flex justify-between items-center mb-3">
                                            <label className="text-gray-700 font-bold text-sm md:text-base">{t('financialCalculators.expectedReturn')}</label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    value={savingsInterest || ""}
                                                    onChange={(e) => setSavingsInterest(e.target.value === "" ? "" : Number(e.target.value))}
                                                    step="0.1"
                                                    className="w-24 md:w-28 py-2 pl-3 pr-7 text-right bg-yellow-50 text-secondary font-bold text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/20"
                                                />
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary font-bold text-sm">%</span>
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            min="1" max="15" step="0.1"
                                            value={savingsInterest}
                                            onChange={(e) => setSavingsInterest(Number(e.target.value))}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-secondary"
                                        />
                                        <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium">
                                            <span>1%</span>
                                            <span>15%</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Results Panel */}
                        <div className="w-full md:w-[420px] bg-gray-50 border-t md:border-t-0 md:border-l border-gray-100 p-5 sm:p-8 md:p-10 flex flex-col items-center justify-center relative">
                            {/* Background Decoration */}
                            <div className="absolute top-0 right-0 w-24 h-24 bg-white rounded-bl-full -z-0"></div>

                            {activeTab === 'emi' ? (
                                <div className="w-full relative z-10 flex flex-col items-center animate-fadeIn">
                                    <div className="text-center mb-5 md:mb-8">
                                        <span className="px-4 py-1 rounded-full bg-gray-100 text-gray-500 text-xs md:text-sm font-semibold mb-3 inline-block">{t('financialCalculators.monthlyEmi')}</span>
                                        <h3 className="text-3xl md:text-5xl font-extrabold text-primary">{formatCurrency(emiResult.emi)}</h3>
                                    </div>

                                    <div className="mb-6 md:mb-10">
                                        <DonutChart
                                            val1={loanAmount}
                                            val2={emiResult.totalInterest}
                                            color1="#1e3a8a"
                                            color2="#ca8a04"
                                            label={t('financialCalculators.interest')}
                                            percentage={emiResult.totalPayment > 0 ? Math.round((emiResult.totalInterest / emiResult.totalPayment) * 100) : 0}
                                        />
                                    </div>

                                    <div className="w-full space-y-3">
                                        <div className="flex justify-between items-center p-3 md:p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                            <div className="flex items-center gap-2 md:gap-3">
                                                <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-primary flex-shrink-0"></div>
                                                <span className="text-gray-600 font-semibold text-sm md:text-base">{t('financialCalculators.principalAmount')}</span>
                                            </div>
                                            <span className="font-bold text-gray-900 text-sm md:text-base">{formatCurrency(loanAmount)}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-3 md:p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                            <div className="flex items-center gap-2 md:gap-3">
                                                <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-secondary flex-shrink-0"></div>
                                                <span className="text-gray-600 font-semibold text-sm md:text-base">{t('financialCalculators.totalInterest')}</span>
                                            </div>
                                            <span className="font-bold text-gray-900 text-sm md:text-base">{formatCurrency(emiResult.totalInterest)}</span>
                                        </div>
                                        <div className="border-t-2 border-dashed border-gray-200 mt-4 pt-4 flex justify-between items-center">
                                            <span className="text-gray-800 font-bold text-base md:text-xl">{t('financialCalculators.totalPayable')}</span>
                                            <span className="text-primary font-extrabold text-lg md:text-2xl">{formatCurrency(emiResult.totalPayment)}</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full relative z-10 flex flex-col items-center animate-fadeIn">
                                    <div className="text-center mb-5 md:mb-8">
                                        <span className="px-4 py-1 rounded-full bg-gray-100 text-gray-500 text-xs md:text-sm font-semibold mb-3 inline-block">{t('financialCalculators.maturityValue')}</span>
                                        <h3 className="text-3xl md:text-5xl font-extrabold text-secondary">{formatCurrency(savingsResult.total)}</h3>
                                    </div>

                                    <div className="mb-6 md:mb-10">
                                        <DonutChart
                                            val1={savingsResult.invested}
                                            val2={savingsResult.interest}
                                            color1="#94a3b8"
                                            color2="#ca8a04"
                                            label={t('financialCalculators.returns')}
                                            percentage={savingsResult.total > 0 ? Math.round((savingsResult.interest / savingsResult.total) * 100) : 0}
                                        />
                                    </div>

                                    <div className="w-full space-y-3">
                                        <div className="flex justify-between items-center p-3 md:p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                            <div className="flex items-center gap-2 md:gap-3">
                                                <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-slate-400 flex-shrink-0"></div>
                                                <span className="text-gray-600 font-semibold text-sm md:text-base">{t('financialCalculators.investedAmount')}</span>
                                            </div>
                                            <span className="font-bold text-gray-900 text-sm md:text-base">{formatCurrency(savingsResult.invested)}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-3 md:p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                            <div className="flex items-center gap-2 md:gap-3">
                                                <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-secondary flex-shrink-0"></div>
                                                <span className="text-gray-600 font-semibold text-sm md:text-base">{t('financialCalculators.estReturns')}</span>
                                            </div>
                                            <span className="font-bold text-gray-900 text-sm md:text-base">{formatCurrency(savingsResult.interest)}</span>
                                        </div>
                                        <div className="border-t-2 border-dashed border-gray-200 mt-4 pt-4 flex justify-between items-center">
                                            <span className="text-gray-800 font-bold text-base md:text-xl">{t('financialCalculators.maturityValue')}</span>
                                            <span className="text-secondary font-extrabold text-lg md:text-2xl">{formatCurrency(savingsResult.total)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FinancialCalculators;
